import classNames from 'clsx';
import flow from 'lodash/flow';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import {
  closeNowPlayingDialog,
  closeNowPlayingDialogAndTrack,
  fetchNowPlayingItems,
} from 'src/common/actions/dialog';
import { logMobileWebActivity } from 'src/common/actions/logging';
import { setPageLoadId } from 'src/common/actions/reporting';
import NowPlaying from 'src/common/components/containerItems/childPresentations/NowPlaying';
import {
  FREESTAR_ENABLED,
  FREESTAR_PRIMIS_VIDEO_ENABLED,
} from 'src/common/constants/experiments/ads';
import { withReactHooks } from 'src/common/decorators/withReactHooks';
import { useStores } from 'src/common/providers';
import cssVariables from 'src/common/styles/variables';
import { getDisplayAdAttributes, gptSlotNames } from 'src/common/utils/ads/config';
import mobileWeb from '../../constants/analytics/categoryActionLabel/mobileWeb';
import { NOW_PLAYING_GUIDE_ID } from '../../constants/dialogs/nowPlaying';
import { NO_RECENTS_TEXT, RECENTS } from '../../constants/localizations/shared';
import NowPlayingContext from '../../contexts/NowPlayingContext';
import { withPalette } from '../../decorators/withPalette';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import {
  selectBottomAdBannerHeight,
  selectIsDiscord,
  selectIsFetching,
  selectIsMobile,
} from '../../selectors/app';
import { selectExperiment, selectIsDesktopNowPlayingEnabled } from '../../selectors/config';
import { selectIsUserSubscribed } from '../../selectors/me';
import { isXXSmall } from '../../utils/breakpoints';
import { getCssStyle } from '../../utils/getCssStyle';
import BottomBanner from '../ads/BottomBanner';
import DialogDisplayAd from '../ads/DialogDisplayAd';
import FreeStarVideoAdContainer from '../ads/FreeStarVideoAdContainer';
import SponsoredRollupProvider from '../ads/SponsoredRollupProvider';
import ContainerItemsLoader from '../containerItems/ContainerItemsLoader';
import ListLayout from '../containerItems/list/ListLayout';
import ContainerTitle from '../containerItems/shared/ContainerTitle';
import CommonDialog from '../shared/dialog/CommonDialog';
import { NavHomeTuneInLink } from '../shared/link/NavHomeTuneInLink';
import ScrollReset from '../shared/ScrollReset';
import css from './nowPlayingDialog.module.scss';

const idPrefix = 'nowPlayingDialog';
const MAX_RECENTS_COUNT = 5;
const defaultDiscordBodyStyles = {
  backgroundColor: cssVariables['--medium-grey'],
};

function getPaletteBodyStyles(paletteColor) {
  return {
    background: `linear-gradient(180deg, ${paletteColor}CC, ${cssVariables['--medium-grey']})`,
  };
}

// exported for testing
export class NowPlayingDialog extends Component {
  static propTypes = {
    // from parent
    routeProps: PropTypes.object,
    breakpoint: PropTypes.number.isRequired,

    // from mapStateToProps/mapDispatchToProps
    isNowPlayingDialogOpen: PropTypes.bool.isRequired,
    actions: PropTypes.object.isRequired,
    currentlyPlayingGuideId: PropTypes.string,
    nowPlayingGuideItem: PropTypes.object.isRequired,
    recentsGuideItem: PropTypes.object.isRequired,
    selectedContentId: PropTypes.string,
    isDesktopNowPlayingEnabled: PropTypes.bool.isRequired,
    isDarkMode: PropTypes.bool,
    isUserSubscribed: PropTypes.bool,
    isDiscord: PropTypes.bool,
    isMobile: PropTypes.bool,
    isAppFetchingData: PropTypes.bool,
    isFreestarEnabled: PropTypes.bool.isRequired,
    isFreestarPrimisVideoEnabled: PropTypes.bool.isRequired,
    sponsorshipEnabled: PropTypes.bool.isRequired,
    canShowAds: PropTypes.bool.isRequired,

    // from withRouter
    history: PropTypes.object,

    // from withUsePalette
    palette: PropTypes.object,
  };

  static contextType = LocationAndLocalizationContext;

  constructor(props) {
    super(props);
    this.handleClose = this.handleClose.bind(this);
    this.scrollToTop = this.scrollToTop.bind(this);
    this.handlePopState = this.handlePopState.bind(this);
  }

  state = {
    scrollHash: 0,
    hasAddedHistoryEntry: false,
  };

  componentDidMount() {
    const { isNowPlayingDialogOpen, actions, isDiscord, isMobile } = this.props;

    if (isDiscord) {
      this.deregisterHistoryListener = this.props.history.listen(this.onHistoryChange);
    }

    // For mobile (non-Discord), add a history entry when dialog opens
    const shouldManageHistoryOnMount = isNowPlayingDialogOpen && isMobile && !isDiscord;
    if (shouldManageHistoryOnMount) {
      this.addHistoryEntry();
    }

    // NOTE: Handles directToNowPlaying reporting scenario...
    // where NowPlayingDialog mounts with isNowPlayingDialogOpen set to true via server
    if (isNowPlayingDialogOpen) {
      // eslint-disable-next-line react/no-did-update-set-state
      actions.logMobileWebActivity(mobileWeb.actions.show, mobileWeb.labels.nowPlayingDialog);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      isNowPlayingDialogOpen,
      actions,
      currentlyPlayingGuideId,
      isAppFetchingData,
      isDiscord,
      isMobile,
    } = this.props;

    const dialogJustOpened = isNowPlayingDialogOpen && !prevProps.isNowPlayingDialogOpen;
    const dialogJustClosed = !isNowPlayingDialogOpen && prevProps.isNowPlayingDialogOpen;
    const shouldManageHistoryForMobile = isMobile && !isDiscord;

    if (dialogJustOpened) {
      // For mobile (non-Discord), add a history entry when dialog opens
      if (shouldManageHistoryForMobile) {
        this.addHistoryEntry();
      }

      // NOTE: Calling fetchNowPlayingItems without currentlyPlayingGuideId will break NowPlaying Containers
      if (currentlyPlayingGuideId) {
        actions.setPageLoadId();
        actions.fetchNowPlayingItems(currentlyPlayingGuideId);
      }

      // eslint-disable-next-line react/no-did-update-set-state
      actions.logMobileWebActivity(mobileWeb.actions.show, mobileWeb.labels.nowPlayingDialog);
      return;
    }

    // Clean up history entry when dialog closes
    if (dialogJustClosed && shouldManageHistoryForMobile) {
      this.removeHistoryEntry();
    }

    // When the NP dialog is open on Discord, close NP if a route change occurred, as indicated by the app data-fetching
    // state changing to false. Using a setTimeout allows rendering to finish on the page prior to closing the NP dialog.
    const shouldCloseOnDiscordRouteChange =
      isNowPlayingDialogOpen && isDiscord && !isAppFetchingData && prevProps.isAppFetchingData;

    if (shouldCloseOnDiscordRouteChange) {
      setTimeout(actions.closeNowPlayingDialog);
    }
  }

  componentWillUnmount() {
    this.deregisterHistoryListener?.();
    this.removePopStateListener();

    // Clean up history entry if component unmounts while dialog is open
    const { isMobile, isDiscord } = this.props;
    const shouldCleanupHistoryOnUnmount = isMobile && !isDiscord && this.state.hasAddedHistoryEntry;

    if (shouldCleanupHistoryOnUnmount) {
      this.removeHistoryEntry();
    }
  }

  addHistoryEntry() {
    if (!this.state.hasAddedHistoryEntry) {
      // Add a history entry so back gesture closes modal instead of navigating
      this.props.history.push(this.props.history.location);
      this.setState({ hasAddedHistoryEntry: true });
      this.addPopStateListener();
    }
  }

  removeHistoryEntry() {
    if (this.state.hasAddedHistoryEntry) {
      this.removePopStateListener();
      this.setState({ hasAddedHistoryEntry: false });
    }
  }

  addPopStateListener() {
    if (typeof window !== 'undefined') {
      window.addEventListener('popstate', this.handlePopState);
    }
  }

  removePopStateListener() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('popstate', this.handlePopState);
    }
  }

  handlePopState = (event) => {
    // Prevent the actual navigation and close the modal instead
    event.preventDefault();
    this.props.actions.closeNowPlayingDialogAndTrack();
  };

  handleClose() {
    this.props.actions.closeNowPlayingDialogAndTrack();
  }

  // Listens to the router history and closes the NP dialog if a user clicks on a link that matches the current route.
  // For example, by clicking on the title in the player (visible on desktop Discord) or the profile link under the
  // title in the NP dialog.
  // If a user clicks on a link that navigates to a *different* route, we have a data-fetching listener in
  // componentDidUpdate that handles closing the NP dialog. We don't handle that here because data-fetching times are
  // variable (e.g., due to network conditions), so we don't want to optimistically close the NP dialog while a stale
  // page is present, which would suddenly be visually replaced by a new page.
  onHistoryChange = (location) => {
    const { actions } = this.props;
    const { locationPathname } = this.state;

    if (locationPathname && locationPathname === location.pathname) {
      actions.closeNowPlayingDialog();
    }

    this.setState({ locationPathname: location.pathname });
  };

  scrollToTop() {
    this.setState((state) => ({
      scrollHash: state.scrollHash + 1,
    }));
  }

  render() {
    const {
      isNowPlayingDialogOpen,
      breakpoint,
      nowPlayingGuideItem,
      recentsGuideItem,
      currentlyPlayingGuideId,
      routeProps,
      isDesktopNowPlayingEnabled,
      isUserSubscribed,
      isDiscord,
      palette,
      isFreestarEnabled,
      isFreestarPrimisVideoEnabled,
      sponsorshipEnabled,
      canShowAds,
      bottomBannerHeight,
    } = this.props;
    const { getLocalizedText } = this.context;

    // if this logic is removed we'll need to deregister the ad slot from mint
    if (!isNowPlayingDialogOpen) {
      return null;
    }

    // NOTE: currentlyPlayingGuideId not avail when leveraging SSR for directToNowPlaying UX
    const guideIdToPlay = get(routeProps, 'guideContext.guideId');
    const activeGuideId = currentlyPlayingGuideId || guideIdToPlay;
    const playerHeight = isDesktopNowPlayingEnabled ? getCssStyle('--player-height') : 0;
    const dialogContentHeight = `${parseFloat(playerHeight) + bottomBannerHeight}px`;

    const matchurl = get(routeProps, 'matchUrl', '');
    const paletteColor = (!palette?.isLoading && !palette?.error && palette?.vibrant) || null;
    const discordBodyStyle = paletteColor
      ? getPaletteBodyStyles(paletteColor)
      : defaultDiscordBodyStyles;
    const bodyStyle = isDiscord ? discordBodyStyle : {};
    const containers = isXXSmall(breakpoint)
      ? nowPlayingGuideItem.containers
      : nowPlayingGuideItem.containers.slice(0, 1);
    const shouldShowInContentVideoAd =
      !isUserSubscribed &&
      canShowAds &&
      !sponsorshipEnabled &&
      isFreestarEnabled &&
      isFreestarPrimisVideoEnabled;

    return (
      <CommonDialog
        modal
        bodyClassName={css.dialog}
        contentClassName={css.dialogContent}
        bodyStyle={bodyStyle}
        overlayClassName={isDesktopNowPlayingEnabled ? css.dialogOverlay : undefined}
        dialogOpen
        handleDialogClose={this.handleClose}
        data-testid={idPrefix}
        hasDarkTheme
        hideCornerClose
      >
        <div className={css.containerPage} style={{ paddingBottom: dialogContentHeight }}>
          <div className={css.badge}>
            <NavHomeTuneInLink />
          </div>
          <NowPlayingContext.Provider value={{ scrollToTop: this.scrollToTop }}>
            <ScrollReset
              className={classNames('scroller', css.scrollReset)}
              scrollHash={this.state.scrollHash}
            >
              <div
                className={classNames(css.leftSide, {
                  [css.desktop]: isDesktopNowPlayingEnabled,
                })}
              >
                <div className={css.containerItems}>
                  {containers ? (
                    <SponsoredRollupProvider guideId={NOW_PLAYING_GUIDE_ID}>
                      <ContainerItemsLoader
                        guideItem={nowPlayingGuideItem}
                        routeProps={routeProps}
                        itemsStyles={get(nowPlayingGuideItem, 'metadata.styles', {})}
                        items={containers}
                        isFetching={false}
                        breakpoint={breakpoint}
                        selectedContentId={activeGuideId}
                        isNowPlaying
                        isDarkMode
                      />
                    </SponsoredRollupProvider>
                  ) : (
                    <NowPlaying
                      isInNowPlayingDialog
                      hideDisplayAd
                      breakpoint={breakpoint}
                      selectedContentId={activeGuideId}
                      matchUrl={get(routeProps, 'matchUrl', '')}
                    />
                  )}
                </div>
              </div>
            </ScrollReset>
            {isDesktopNowPlayingEnabled && (
              <div className={css.rightSide}>
                {shouldShowInContentVideoAd && <FreeStarVideoAdContainer isInNowPlayingDialog />}
                {!isUserSubscribed && (
                  <div data-darkmode>
                    <DialogDisplayAd
                      isProfile={false}
                      matchurl={matchurl}
                      guideId={activeGuideId}
                      classname={css.ad}
                      {...getDisplayAdAttributes(gptSlotNames.nowPlaying_side, isFreestarEnabled)}
                    />
                  </div>
                )}
                {isUserSubscribed && recentsGuideItem.children && (
                  <div data-darkmode>
                    <ListLayout
                      breakpoint={breakpoint}
                      selectedContentId={activeGuideId}
                      matchUrl={matchurl}
                      id="nowPlayingRecents"
                      containerItem={recentsGuideItem}
                      containerTitle={getLocalizedText(RECENTS)}
                      maxChildren={MAX_RECENTS_COUNT}
                      containerTitleClassName={css.recents}
                    />
                  </div>
                )}
                {isUserSubscribed && !recentsGuideItem.children && (
                  <div data-darkmode>
                    <ContainerTitle
                      containerItem={recentsGuideItem}
                      containerTitle={getLocalizedText(RECENTS)}
                      isNowPlayingDialogOpen
                      actions={{}}
                      className={css.recents}
                    />
                    <div className={css.noRecentsSubtitle}>{getLocalizedText(NO_RECENTS_TEXT)}</div>
                  </div>
                )}
              </div>
            )}
          </NowPlayingContext.Provider>
          <BottomBanner isNowPlayingBanner />
        </div>
      </CommonDialog>
    );
  }
}

// exported for unit testing
export function mapStateToProps(state) {
  const isNowPlayingDialogOpen = get(state, 'dialog.isNowPlayingDialogOpen', false);
  const { dialog } = state;
  const nowPlayingGuideItem = get(dialog, NOW_PLAYING_GUIDE_ID, {});
  const recentsGuideItem = get(dialog, 'recents', {});

  return {
    isNowPlayingDialogOpen,
    nowPlayingGuideItem,
    recentsGuideItem,
    isDesktopNowPlayingEnabled: selectIsDesktopNowPlayingEnabled(state),
    isUserSubscribed: selectIsUserSubscribed(state),
    isDiscord: selectIsDiscord(state),
    isMobile: selectIsMobile(state),
    isAppFetchingData: selectIsFetching(state),
    isFreestarEnabled: selectExperiment(state, FREESTAR_ENABLED),
    isFreestarPrimisVideoEnabled: selectExperiment(state, FREESTAR_PRIMIS_VIDEO_ENABLED),
    bottomBannerHeight: selectBottomAdBannerHeight(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        closeNowPlayingDialogAndTrack,
        closeNowPlayingDialog,
        logMobileWebActivity,
        fetchNowPlayingItems,
        setPageLoadId,
      },
      dispatch,
    ),
  };
}

export default flow(
  withPalette,
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withReactHooks(() => {
    const { playerStore } = useStores();

    return {
      currentlyPlayingGuideId: playerStore.currentGuideItem()?.playableGuideId,
    };
  }),
)(NowPlayingDialog);
